import { CNS_API_BASE } from "@/config";
import type {
  CNSSourceCredential,
  CNSSourceCredentialOAuthResult,
  CNSSourceCredentialOAuthStart,
  CNSSourceCredentialPage,
  CNSSourceProvider,
} from "@/models/sourceConnection";
import type { ConnectorOAuthStartContext } from "@/models/connectorOAuth";
import { SourceOAuthError } from "@/lib/sources/sourceOAuthErrors";
import { authorizeSourceCredentialOAuth } from "@/services/connectorOAuthService";
import {
  getProblemDetail,
  getProblemDetailCode,
  throwOnProblemResponse,
} from "@/types/problem-detail";

/** Lists configured, non-secret source provider metadata. */
export async function fetchSourceProviders(): Promise<CNSSourceProvider[]> {
  const response = await fetch(`${CNS_API_BASE}/source-providers`, {
    credentials: "include",
  });
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load plugin providers");
  }
  return response.json();
}

/** Lists source credentials owned by the current user in a workspace. */
export async function fetchSourceCredentials(
  workspaceId: string,
): Promise<CNSSourceCredential[]> {
  const params = new URLSearchParams({ page: "0", size: "100", sort: "provider,asc" });
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${workspaceId}/source-credentials?${params}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load plugins");
  }
  const page = (await response.json()) as CNSSourceCredentialPage;
  return page.content ?? [];
}

/** Creates a pending source credential for a configured provider. */
export async function createSourceCredential(
  workspaceId: string,
  provider: string,
): Promise<CNSSourceCredential> {
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${workspaceId}/source-credentials`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider }),
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to prepare plugin");
  }
  return response.json();
}

/**
 * Returns the current user's credential for a provider, creating one when absent.
 *
 * Recovers concurrent creates via SOURCE_CREDENTIAL_EXISTS. Re-throws HTTP 401 so callers can
 * surface session expiry instead of mapping it to PLUGIN_PREPARE_FAILED.
 *
 * @throws {SourceOAuthError} PLUGIN_PREPARE_FAILED when create fails for a non-401, non-exists reason
 */
export async function ensureSourceCredential(
  workspaceId: string,
  provider: string,
): Promise<CNSSourceCredential> {
  const existing = (await fetchSourceCredentials(workspaceId)).filter(
    (credential) => credential.provider === provider,
  );
  if (existing.length === 1) {
    return existing[0];
  }
  if (existing.length > 1) {
    throw new SourceOAuthError("PLUGIN_PREPARE_FAILED");
  }

  try {
    return await createSourceCredential(workspaceId, provider);
  } catch (error) {
    if (getProblemDetail(error)?.status === 401) {
      throw error;
    }
    if (getProblemDetailCode(error) === "SOURCE_CREDENTIAL_EXISTS") {
      const refreshed = (await fetchSourceCredentials(workspaceId)).filter(
        (credential) => credential.provider === provider,
      );
      if (refreshed.length === 1) {
        return refreshed[0];
      }
    }
    throw new SourceOAuthError("PLUGIN_PREPARE_FAILED");
  }
}

/** Creates a durable OAuth attempt and returns its public ID and provider URL. */
export async function startSourceCredentialOAuth(
  credentialId: string,
  context?: ConnectorOAuthStartContext,
  signal?: AbortSignal,
): Promise<CNSSourceCredentialOAuthStart> {
  return authorizeSourceCredentialOAuth(credentialId, context, signal);
}

/** Reads the credential status after an OAuth popup completes or closes. */
export async function fetchSourceCredentialOAuthResult(
  credentialId: string,
): Promise<CNSSourceCredentialOAuthResult> {
  const response = await fetch(
    `${CNS_API_BASE}/source-credentials/${credentialId}/oauth/complete`,
    { credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to confirm plugin authorization");
  }
  return response.json();
}

/** Disconnects a source credential while preserving reusable client registration metadata. */
export async function revokeSourceCredential(
  credentialId: string,
): Promise<CNSSourceCredential> {
  const response = await fetch(
    `${CNS_API_BASE}/source-credentials/${credentialId}/revoke`,
    { method: "POST", credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to disconnect plugin");
  }
  return response.json();
}

/** Clears both OAuth grant and local registration state for exceptional connection repair. */
export async function resetSourceCredential(
  credentialId: string,
): Promise<CNSSourceCredential> {
  const response = await fetch(
    `${CNS_API_BASE}/source-credentials/${credentialId}/reset`,
    { method: "POST", credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to reset plugin");
  }
  return response.json();
}
